import React, { useEffect, useState } from 'react';
import { fetchMonthlyOeeSchedule } from '../api';
import MonthlyWorkcellOeeScheduler from '../components/MonthlyWorkcellOeeScheduler';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useParams, useNavigate } from 'react-router-dom';
import MonthlyViewLayout from '../components/MonthlyViewLayout';
import { useTranslation } from "react-i18next";
import { useCustomerLocale } from "../context/CustomerSettingsContext";
import { formatLocalDate, parseLocalDateTime } from "../utils/localDateTime";

const getMonthStart = (date) => {
  const d = parseLocalDateTime(date) || new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const MonthlyWorkcellOeeSchedulerPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const locale = useCustomerLocale();
  const initialDate = params.date ? getMonthStart(params.date) : getMonthStart(formatLocalDate(new Date()));
  const [date, setDate] = useState(initialDate);
  const [oeeRequest, setOeeRequest] = useState({ date: initialDate, status: 'loading', data: [] });

  useEffect(() => {
    let cancelled = false;
    setOeeRequest({ date, status: 'loading', data: [] });
    fetchMonthlyOeeSchedule(date)
      .then((data) => {
        if (!cancelled) setOeeRequest({ date, status: 'success', data });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error loading monthly OEE:', error);
        setOeeRequest({ date, status: 'error', data: [] });
      });
    return () => { cancelled = true; };
  }, [date]);

  const isLoading = oeeRequest.date !== date || oeeRequest.status === 'loading';

  const handleMonthChange = (delta) => {
    const d = parseLocalDateTime(`${date}T00:00:00`);
    d.setMonth(d.getMonth() + delta);
    const newDate = getMonthStart(d);
    setDate(newDate);
    navigate(`/chart/monthly/oee/${newDate}`);
  };

  return (
    <MonthlyViewLayout date={date}>
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton onClick={() => handleMonthChange(-1)}><ArrowBackIosIcon /></IconButton>
        <Typography variant="h5" flex={1} align="center">
          {t("monthly.oeeScheduler.title", {
            monthYear: parseLocalDateTime(`${date}T00:00:00`)?.toLocaleString(locale, { month: 'long', year: 'numeric' }) || date
          })}
        </Typography>
        <IconButton onClick={() => handleMonthChange(1)}><ArrowForwardIosIcon /></IconButton>
      </Box>
      {isLoading || oeeRequest.status === 'error' ? (
        <Box sx={{ minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
          <Typography>{t(isLoading ? 'common.loading' : 'common.failedToLoadData')}</Typography>
        </Box>
      ) : (
        <MonthlyWorkcellOeeScheduler oeeData={oeeRequest.data} date={date} onDateChange={d => {
          setDate(getMonthStart(d));
          navigate(`/chart/monthly/oee/${getMonthStart(d)}`);
        }} />
      )}
    </MonthlyViewLayout>
  );
};

export default MonthlyWorkcellOeeSchedulerPage;
